'use client'

import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Input, KiaoModal, Picker } from '@kiao-voice/ui-kit'
import type { DictionarySummary } from '@/features/dictionary/types'

/** The picker value that means "file this into a dictionary I name here". */
export const SAVE_TARGET_NEW = 'new'

export interface SaveTermDialogProps {
  /** The term being filed; null closes the dialog. */
  term: string | null
  dictionaries: DictionarySummary[]
  /** A dictionary id as a string, or `SAVE_TARGET_NEW`. */
  target: string
  onTargetChange: (target: string) => void
  /** The name for a dictionary about to be created. */
  name: string
  onNameChange: (name: string) => void
  /** A save is in flight — the whole form is inert until it settles. */
  busy: boolean
  error: string | null
  onSubmit: () => void
  onClose: () => void
}

/**
 * File one hand-typed term into a dictionary, either an existing one or a new
 * one named here, so the next upload gets it without retyping.
 */
export function SaveTermDialog({
  term,
  dictionaries,
  target,
  onTargetChange,
  name,
  onNameChange,
  busy,
  error,
  onSubmit,
  onClose,
}: SaveTermDialogProps) {
  const { t } = useTranslation('web')
  const nameInput = useRef<HTMLInputElement>(null)
  if (term === null) return null
  return (
    <KiaoModal
      open
      onClose={onClose}
      title={t('batchTranscription.saveDialogTitle', { term })}
      closeLabel={t('transcriptions.upload.close')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('transcriptions.upload.cancel')}
          </Button>
          <Button disabled={busy} onClick={onSubmit}>
            {t('batchTranscription.saveDialogSubmit')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Picker
          label={t('batchTranscription.saveDialogPickerLabel')}
          value={target === SAVE_TARGET_NEW ? null : target}
          placeholder={name.trim() || t('batchTranscription.saveDialogNewOption')}
          disabled={busy}
          searchThreshold={0}
          searchPlaceholder={t('batchTranscription.saveDialogNamePlaceholder')}
          emptyLabel={t('batchTranscription.dictionaryPickerEmpty')}
          onChange={onTargetChange}
          options={dictionaries.map((dictionary) => ({
            value: String(dictionary.id),
            label: dictionary.name,
          }))}
          createOption={{
            label: (query) => `${t('batchTranscription.saveDialogNewOption')}：${query}`,
            onCreate: (query) => {
              onNameChange(query)
              onTargetChange(SAVE_TARGET_NEW)
              // The input may already exist when there are no dictionaries yet.
              requestAnimationFrame(() => nameInput.current?.focus())
            },
          }}
        />
        {target === SAVE_TARGET_NEW ? (
          <Input
            ref={nameInput}
            autoFocus
            type="text"
            aria-label={t('batchTranscription.saveDialogNameLabel')}
            placeholder={t('batchTranscription.saveDialogNamePlaceholder')}
            value={name}
            disabled={busy}
            onChange={(event) => onNameChange(event.target.value)}
          />
        ) : null}
        {error ? <p className="text-xs leading-5 text-[var(--error)]">{error}</p> : null}
      </div>
    </KiaoModal>
  )
}
